import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";

import { Surface, DataTable, FilterBar, StatusChip } from "components/ui";
import { T, inr } from "theme/tokens";
import * as api from "../api";

const when = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
};

/**
 * Every delivery this partner carried.
 *
 * Filter options come from the server rather than a hardcoded list: `status`
 * has no enum in the orders schema, so a fixed dropdown would silently be
 * unable to filter for a value the data actually contains.
 */
export default function OrdersTab({ partnerId }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 0, pageSize: 25 });

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const [options, setOptions] = useState({ statuses: [], paymentStatuses: [] });

  useEffect(() => {
    api
      .getOrderFilters(partnerId)
      .then((data) =>
        setOptions({
          statuses: data?.statuses || [],
          paymentStatuses: data?.paymentStatuses || [],
        })
      )
      .catch(() => setOptions({ statuses: [], paymentStatuses: [] }));
  }, [partnerId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getOrders(partnerId, {
        page: pagination.page + 1,
        limit: pagination.pageSize,
        search: search.trim() || undefined,
        status,
        paymentStatus,
        from,
        to,
      });
      setRows(data?.orders || []);
      setTotal(data?.pagination?.total || 0);
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "Could not load deliveries."
      );
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [partnerId, pagination, search, status, paymentStatus, from, to]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(
    () => [
      {
        field: "orderId",
        headerName: "Order",
        flex: 1,
        minWidth: 170,
        sortable: false,
        renderCell: ({ row }) => (
          <Box sx={{ py: 0.6, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: T.ink }}>
              {row.orderId || "—"}
            </Typography>
            <Typography sx={{ fontSize: "0.7rem", color: T.subtle }}>
              {when(row.createdAt)}
            </Typography>
          </Box>
        ),
      },
      {
        field: "customerName",
        headerName: "Customer",
        flex: 1,
        minWidth: 160,
        sortable: false,
        renderCell: ({ row }) => (
          <Box sx={{ py: 0.6, minWidth: 0 }}>
            <Typography sx={{ fontSize: "0.82rem", color: T.ink }}>
              {row.customerName || "—"}
            </Typography>
            <Typography sx={{ fontSize: "0.7rem", color: T.subtle, fontFamily: "monospace" }}>
              {row.customerPhone || "—"}
            </Typography>
          </Box>
        ),
      },
      {
        field: "status",
        headerName: "Status",
        width: 150,
        sortable: false,
        renderCell: ({ row }) => <StatusChip status={row.status} />,
      },
      {
        field: "payment",
        headerName: "Payment",
        width: 150,
        sortable: false,
        renderCell: ({ row }) => (
          <Stack spacing={0.3} sx={{ py: 0.6 }} alignItems="flex-start">
            <StatusChip status={row.payment?.status} label={row.payment?.status || "—"} />
            <Typography sx={{ fontSize: "0.68rem", color: T.subtle }}>
              {row.payment?.method || "—"}
            </Typography>
          </Stack>
        ),
      },
      {
        field: "totalPrice",
        headerName: "Value",
        width: 120,
        sortable: false,
        renderCell: ({ row }) => (
          <Typography
            sx={{
              fontSize: "0.85rem",
              fontWeight: 600,
              color: T.ink,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {/* totalPrice is a String in the orders schema. */}
            {inr(Number(row.totalPrice || 0))}
          </Typography>
        ),
      },
      {
        field: "deliveryType",
        headerName: "Type",
        width: 130,
        sortable: false,
        renderCell: ({ row }) => (
          <Typography sx={{ fontSize: "0.78rem", color: T.muted }}>
            {row.deliveryType || "—"}
          </Typography>
        ),
      },
    ],
    []
  );

  const resetPage = () => setPagination((p) => ({ ...p, page: 0 }));

  return (
    <Surface sx={{ p: { xs: 1.5, md: 2 } }}>
      <FilterBar
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          resetPage();
        }}
        searchPlaceholder="Order ID, customer name or phone…"
        from={from}
        to={to}
        onFromChange={(v) => {
          setFrom(v);
          resetPage();
        }}
        onToChange={(v) => {
          setTo(v);
          resetPage();
        }}
        selects={[
          {
            key: "status",
            label: "Status",
            value: status,
            options: options.statuses.map((s) => ({ value: s, label: s })),
            onChange: (v) => {
              setStatus(v);
              resetPage();
            },
          },
          {
            key: "paymentStatus",
            label: "Payment",
            value: paymentStatus,
            options: options.paymentStatuses.map((s) => ({ value: s, label: s })),
            onChange: (v) => {
              setPaymentStatus(v);
              resetPage();
            },
          },
        ]}
        onRefresh={load}
        onReset={() => {
          setSearch("");
          setStatus("");
          setPaymentStatus("");
          setFrom("");
          setTo("");
          setPagination({ page: 0, pageSize: 25 });
        }}
      />

      <DataTable
        rows={rows}
        columns={columns}
        loading={loading}
        error={error}
        onRetry={load}
        emptyMessage="This partner has not carried any deliveries in this range."
        rowCount={total}
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        getRowId={(row) => row._id || row.orderId}
        height={560}
      />
    </Surface>
  );
}
